import React from 'react'
import { InfoWindow, GoogleMap, Marker, MarkerClusterer, useJsApiLoader } from '@react-google-maps/api';
import { graphql, useStaticQuery } from "gatsby"
import ArtworkCard from './artwork-card';
import Link, { ExternalLink } from './link';
import AppStoreBadges from './AppStoreBadges';
import Text from './text';

function GoogleMapComponent({id, coords, title, artworks,}: {
  id?: string;
  coords?: string;
  title?: string;
  artworks?: {}[];
}) {
  const {allArtwork: {nodes: allArtworks}} = useStaticQuery(graphql`
    {
      allArtwork {
        nodes {
            id
            coords
            title
            artist
            location
            image_url
        }
      }
    }
  `)

  const infoWindowRef = React.useRef(null)
  const filterArtworks = React.useCallback(() => {
      return !!id 
        ? allArtworks.filter(({id: markerId}: {id: string}) => id !== markerId)
        : artworks ? artworks : allArtworks
  }, [id, artworks])

  const [infoWindow, setInfowindow] = React.useState(false)

  const viewingArtwork = allArtworks.find(a => a.id === id)

  const center = !!id ? latLngFromArtworkCoords(coords) : {lat: 47.56377427794812, lng: -52.7337847302016}

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: "AIzaSyA861ZJQw51Cd2VwziVd-8-s93w1v_VoGM"
  })

  const [map, setMap] = React.useState(null)

  const onLoad = React.useCallback(function callback(map) {
    const bounds = new window.google.maps.LatLng(center);
    map.setCenter(bounds);
    setMap(map)
  }, [])

  const onUnmount = React.useCallback(function callback(map) {
    setMap(null)
  }, [id])

  return isLoaded ? (
      <GoogleMap
        clickableIcons={false}
        mapContainerStyle={{
          width: '100%',
          height: '800px',
        }}
        center={center}
        zoom={!!id ? 12 : 10}
        onLoad={onLoad}
        onUnmount={onUnmount}
      >
        {!!infoWindow && <InfoWindow 
          ref={infoWindowRef} 
          position={latLngFromArtworkCoords(infoWindow.coords)}
          onCloseClick={() => setInfowindow(false)}
        >
          <div>
            <ArtworkCard art={infoWindow} />
            <Link ssx={{fontSize: 1, ml: 3, textDecoration: 'underline',}} to={`/artwork/${infoWindow.id}`}>More info</Link>
          </div>
        </InfoWindow>}
        <MarkerClusterer minimumClusterSize={4}>
          {clusterer => filterArtworks().map(({id, coords,}, i) => <Marker 
            key={id}
            onClick={() => {
              setInfowindow(filterArtworks().find(a => a.id === id))
            }}
            position={latLngFromArtworkCoords(coords)}
            clusterer={clusterer}
          >
          </Marker>)}</MarkerClusterer>

        {!!id && <>
          <Marker position={latLngFromArtworkCoords(coords)} />
          <InfoWindow 
            options={{
              pixelOffset: new window.google.maps.Size(0, -20)
            }}
            position={latLngFromArtworkCoords(viewingArtwork.coords)}
          >
            <div>
              <ArtworkCard fullWidth art={viewingArtwork} hideLocation />
              <Text small>Public Art App: Directions and fun stuff</Text>
              <AppStoreBadges />

              <ExternalLink target="_blank" ssx={{my: 2}} href={`https://www.google.com/maps/dir/?api=1&destination=${latLngFromArtworkCoords(viewingArtwork.coords).lat}%2C${latLngFromArtworkCoords(viewingArtwork.coords).lng}`}>Google Maps Directions</ExternalLink>
            </div>
          </InfoWindow>
        </>}
      </GoogleMap>
  ) : <></>
}

function latLngFromArtworkCoords(coords) {
    return {lat: Number(coords.split(',')[1]), lng: Number(coords.split(',')[0])}
}

export default React.memo(GoogleMapComponent)